import { Form, Formik } from "formik";
import { Button, IconButton } from "gatsby-theme-material-ui";
import React, { ReactElement, useEffect } from "react";
import PropTypes from "prop-types";
import {
  Divider,
  FormControlLabel,
  Hidden,
  Radio,
  RadioGroup,
  Theme,
  InputLabel,
  FormControl,
  Checkbox,
} from "@material-ui/core";
import ButtonMaterial from "@material-ui/core/Button";
import NativeSelect from "@material-ui/core/NativeSelect";
import { createStyles, makeStyles, useTheme } from "@material-ui/core/styles";
import { Search as SearchIcon } from "@material-ui/icons";
import { navigate } from "gatsby";

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      borderRadius: 10,
      paddingBottom: 25,
      alignItems: "center",
      background: theme.palette.background.default,
      display: "grid",
      "& .MuiFormControlLabel-label": {
        marginRight: 4,
      },
      "& .MuiInputLabel-formControl": {
        transform: "none",
        marginTop: 4
      },
    },
    maxWidth800: {
      maxWidth: 800,
      paddingBottom: 0,
    },
    regularSearch: {
      display: "grid",
      gap: `${theme.spacing(1)}px`,
      padding: theme.spacing(1),
      [theme.breakpoints.down("sm")]: {
        gridTemplateRows: `auto auto auto`,
      },
      [theme.breakpoints.up("sm")]: {
        gridTemplateColumns: "auto auto 4fr auto",
      },
      "& .MuiFormControlLabel-labelPlacementStart": {
        marginLeft: 4,
        marginRight: 0
      }
    },
    advancedSearch: {
      display: "grid",
      gridAutoFlow: "column",
      justifyContent: "space-around",
      padding: 15,
      alignItems: "baseline",
      gap: "10px",
    },
    hidden: {
      display: "none",
    },
    autoComplete: {
      display: "grid",
      alignItems: "center",
      [theme.breakpoints.down("sm")]: {
        // gridColumn: "span 6"
      },
    },
    iconButton: {
      display: "none",
    },
    radioGroup: {
      justifyContent: "center",
      [theme.breakpoints.down("sm")]: {
        // gridColumn: "span 6"
        // order: 3
      },
    },
    selectors: {
      display: "grid",
      gridTemplateColumns: "1fr 1fr 1fr auto",
      "& .MuiInput-formControl": {
        [theme.breakpoints.up("sm")]: {
          marginRight: 8,
        },
      },
    },
    selectorsWithSort: {
      display: "grid",
      [theme.breakpoints.up("sm")]: {
        gridTemplateColumns: "2fr 2fr 2fr 1fr 1fr",
      },
      [theme.breakpoints.down("sm")]: {
        gap: '10px'
      },
      "& .MuiInput-formControl": {
        marginRight: 10,
        [theme.breakpoints.up("sm")]: {
          marginBottom: 16,
          marginRight: 30,
        },
      },
      "& .MuiFormControlLabel-root": {
        marginLeft: -4
      }
    },
    submit: { height: "100%", width: "100%" },
    underline: {
      width: "100%",
      "& div:before": {
        display: "none",
        height: 0,
        width: 0,
      },
    },
    sorted: {
      [theme.breakpoints.up("sm")]: {
        flexDirection: "row",
        alignItems: "center",
        gap: "20px",
      },
    },
    sortContainer: {
      [theme.breakpoints.up("sm")]: {
        display: "grid",
        paddingLeft: "20px",
        justifyContent: "start",
      },
    },
    searchBtn: {
      marginTop: 12,
      marginLeft: -10,
    }
  })
);

export const PropertySearch = ({
  withSort = false,
  filterOptions = {
    sell: "Buy",
    beds: "",
    minPrice: "",
    maxPrice: "",
    STC: false,
    propertyType: "",
    sortProperty: "highest"
  },
  length=0,
  handleSubmitFilters
}): ReactElement => {
  const classes = useStyles();
  const [filters, setFilterOptions] = React.useState(filterOptions);
  const [STC, setSTC] = React.useState(filterOptions.STC);
  const [isAdvanced, setIsAdvanced] = React.useState(false);

  let locationQuery = typeof window !== "undefined" ? window.location.search : "";

  useEffect(() => {
    const sell = location.search.slice(1);
    if(sell === 'Buy') {
      setSTC(false);
      setFilterOptions({
        beds: "",
        minPrice: "",
        maxPrice: "",
        STC: false,
        propertyType: "",
        sortProperty: "highest",
        sell: "Buy"
      });
    } else if(sell === 'Rent') {
      setSTC(false);
      setFilterOptions({
        beds: "",
        minPrice: "",
        maxPrice: "",
        STC: false,
        propertyType: "",
        sortProperty: "newest",
        sell: "Rent"
      });
    }
  }, [locationQuery])

  const handleSellOption = (event): void => {
    event.target.value === 'Buy' ? 
      setFilterOptions({
        sell: "Buy",
        beds: "",
        minPrice: "",
        maxPrice: "",
        STC: false,
        propertyType: "",
        sortProperty: "highest",
      }) :
      setFilterOptions({
        sell: "Rent",
        beds: "",
        minPrice: "",
        maxPrice: "",
        STC: false,
        propertyType: "",
        sortProperty: "newest",
      })
  };

  const handleBedsOption = (event): void => {
    setFilterOptions({
      ...filters,
      beds: event.target.value,
    });
  };

  const handleMinPriceOption = (event): void => {
    setFilterOptions({
      ...filters,
      minPrice: event.target.value,
    });
  };

  const handleMaxPriceOption = (event): void => {
    setFilterOptions({
      ...filters,
      maxPrice: event.target.value,
    });
  };

  const handlePropertyOption = (event): void => {
    setFilterOptions({
      ...filters,
      propertyType: event.target.value,
    });
  };

  const handleSortOption = (event): void => {
    setFilterOptions({
      ...filters,
      sortProperty: event.target.value
    });
    const filterOptions = {
      ...filters,
      STC: STC,
      sortProperty: event.target.value
    }
    handleSubmitFilters(filterOptions);
  };

  const handleOfferStatus = (): void => {
    setSTC(!STC);
    const filterOptions = {
      ...filters,
      STC: !STC
    }
    if (withSort) {
      handleSubmitFilters(filterOptions);
    }
  }

  function onSubmitHandler() {
    const filterOptions = {
      ...filters,
      STC: STC
    }
    if(!withSort) {
      navigate("/result/", {
        state: { filterOptions },
      })
    } else {
      handleSubmitFilters(filterOptions);
    }
  };
  return (
    <Formik
      initialValues={{
        "property-type": "Buy",
      }}
      onSubmit={onSubmitHandler}
    >
      {({ submitForm }): ReactElement => (
        <Form
          className={classes.root + " " + (!withSort && classes.maxWidth800)}
        >
          <div
            style={{ display: "grid", gridTemplateRows: withSort && "1fr auto" }}
          >
            <div className={classes.regularSearch}>
              <RadioGroup
                aria-label="position"
                className={classes.radioGroup}
                name="property-type"
                onChange={handleSellOption}
                row
                value={filters?.sell}
              >
                <FormControlLabel
                  control={<Radio />}
                  label="Buy"
                  labelPlacement="start"
                  value="Buy"
                />
                <FormControlLabel
                  control={<Radio />}
                  label="Rent"
                  labelPlacement="start"
                  value="Rent"
                />
              </RadioGroup>

              <Hidden smDown>
                <Divider orientation="vertical" />
              </Hidden>
              <div
                className={
                  withSort ? classes.selectorsWithSort : classes.selectors
                }
              >
                <FormControl>
                  <InputLabel htmlFor="minPrice">Min Price</InputLabel>
                  <NativeSelect
                    value={filters?.minPrice}
                    onChange={handleMinPriceOption}
                    inputProps={{
                      name: "minPrice",
                      id: "minPrice",
                    }}
                  >
                    <option aria-label="None" value="">
                      No Minimum
                    </option>
                    {filters?.sell === "Buy" ? (
                      <>
                        <option value={100000}>£100,000</option>
                        <option value={200000}>£200,000</option>
                        <option value={300000}>£300,000</option>
                        <option value={500000}>£500,000</option>
                        <option value={750000}>£750,000</option>
                        <option value={1000000}>£1,000,000</option>
                        <option value={2000000}>£2,000,000</option>
                        <option value={3000000}>£3,000,000</option>
                        <option value={5000000}>£5,000,000</option>
                        <option value={7000000}>£7,000,000</option>
                        <option value={10000000}>£10,000,000</option>
                      </>
                    ) : (
                        <>
                          <option value={900}>£900pcm (£208pw)</option>
                          <option value={1000}>£1000pcm (£231pw)</option>
                          <option value={1250}>£1,250pcm (£289pw)</option>
                          <option value={2000}>£2,000pcm (£462pw)</option>
                          <option value={3000}>£3,000pcm (£693pw)</option>
                          <option value={4000}>£4,000pcm (£924pw)</option>
                          <option value={5000}>£5,000pcm (£1,154pw)</option>
                          <option value={6000}>£6,000pcm (£1,385pw)</option>
                          <option value={7000}>£7,000pcm (£1,616pw)</option>
                          <option value={8000}>£8,000pcm (£1,847pw)</option>
                          <option value={9000}>£9,000pcm (£2,077pw)</option>
                          <option value={10000}>£10,000pcm (£2,308pw)</option>
                        </>
                      )}
                  </NativeSelect>
                </FormControl>
                <FormControl>
                  <InputLabel htmlFor="maxPrice">Max Price</InputLabel>
                  <NativeSelect
                    value={filters?.maxPrice}
                    onChange={handleMaxPriceOption}
                    inputProps={{
                      name: "maxPrice",
                      id: "maxPrice",
                    }}
                  >
                    <option aria-label="None" value="">
                      No Maximum
                    </option>
                    {filters?.sell === "Buy" ? (
                      <>
                        <option value={100000}>£100,000</option>
                        <option value={200000}>£200,000</option>
                        <option value={300000}>£300,000</option>
                        <option value={500000}>£500,000</option>
                        <option value={750000}>£750,000</option>
                        <option value={1000000}>£1,000,000</option>
                        <option value={2000000}>£2,000,000</option>
                        <option value={3000000}>£3,000,000</option>
                        <option value={5000000}>£5,000,000</option>
                        <option value={7000000}>£7,000,000</option>
                        <option value={10000000}>£10,000,000</option>
                      </>
                    ) : (
                        <>
                          <option value={900}>£900pcm (£208pw)</option>
                          <option value={1000}>£1000pcm (£231pw)</option>
                          <option value={1250}>£1,250pcm (£289pw)</option>
                          <option value={2000}>£2,000pcm (£462pw)</option>
                          <option value={3000}>£3,000pcm (£693pw)</option>
                          <option value={4000}>£4,000pcm (£924pw)</option>
                          <option value={5000}>£5,000pcm (£1,154pw)</option>
                          <option value={6000}>£6,000pcm (£1,385pw)</option>
                          <option value={7000}>£7,000pcm (£1,616pw)</option>
                          <option value={8000}>£8,000pcm (£1,847pw)</option>
                          <option value={9000}>£9,000pcm (£2,077pw)</option>
                          <option value={10000}>£10,000pcm (£2,308pw)</option>
                        </>
                      )}
                  </NativeSelect>
                </FormControl>
                <FormControl>
                  <InputLabel htmlFor="beds">Beds</InputLabel>
                  <NativeSelect
                    value={filters?.beds}
                    onChange={handleBedsOption}
                    inputProps={{
                      name: "beds",
                      id: "beds",
                    }}
                  >
                    <option aria-label="None" value="">
                      No Minimum
                    </option>
                    <option value={0}>Studio</option>
                    <option value={1}>1 Bedroom</option>
                    <option value={2}>2 Bedrooms</option>
                    <option value={3}>3 Bedrooms</option>
                    <option value={4}>4 Bedrooms</option>
                  </NativeSelect>
                </FormControl>
                {withSort && (
                  <FormControl>
                    <InputLabel htmlFor="property">Property Type</InputLabel>
                    <NativeSelect
                      value={filters?.propertyType}
                      onChange={handlePropertyOption}
                      inputProps={{
                        name: "property",
                        id: "property",
                      }}
                    >
                      <option aria-label="None" value="">
                        All
                      </option>
                      <option value={1}>House</option>
                      <option value={0}>Flat / Apartment</option>
                    </NativeSelect>
                  </FormControl>
                )}
                {withSort && (
                  <FormControl>
                    <InputLabel htmlFor="sort-by">Sort By</InputLabel>
                    <NativeSelect
                      value={filters?.sortProperty}
                      onChange={handleSortOption}
                      inputProps={{
                        name: "sortProperty",
                        id: "sortProperty",
                      }}
                    >
                      <option value={"lowest"}>Lowest Price</option>
                      <option value={"highest"}>Highest Price</option>
                      <option value={"newest"}>Newest Properties</option>
                    </NativeSelect>
                  </FormControl>
                )}
                {withSort && (
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={STC}
                        onChange={handleOfferStatus}
                        name="checkedB"
                        color="secondary"
                      />
                    }
                    labelPlacement="end"
                    label={`Include ${filters?.sell === "Buy" ? "Sold" : "Let"}/STC`}
                  />
                )}
              </div>

              <Hidden smDown>
                <div className={withSort ? '' : classes.searchBtn}>
                  {withSort && (<InputLabel htmlFor="search">Search</InputLabel>)}
                  <IconButton aria-label="search" onClick={submitForm}>
                    <SearchIcon />
                  </IconButton>
                </div>
              </Hidden>

              <Hidden smUp>
                <Button
                  aria-label="search"
                  variant="contained"
                  onClick={submitForm}
                >
                  Search
                </Button>
              </Hidden>
            </div>

            <div
              className={classes.sortContainer}
            >
              {withSort && (
                <FormControl
                  className={classes.sorted}
                >
                  <InputLabel
                    htmlFor="sortProperty"
                    style={{ position: "static", marginRight: 50 }}
                  >
                    {" "}
                    <span>{length} results</span>
                  </InputLabel>
                </FormControl>
              )}
            </div>
          </div>
          <ButtonMaterial
            color="default"
            style={{
              justifySelf: "center",
              textDecoration: "underline",
              color: "rgba(0, 0, 0, 0.87)",
              paddingBottom: 15,
              display: withSort && "none",
            }}
            onClick={() => setIsAdvanced(!isAdvanced)}
          >
            Advanced Search
          </ButtonMaterial>
          <div
            className={
              classes.advancedSearch + " " + (!isAdvanced && classes.hidden)
            }
          >
            <FormControl>
              <InputLabel htmlFor="property">Property Type</InputLabel>
              <NativeSelect
                value={filters?.propertyType}
                onChange={handlePropertyOption}
                inputProps={{
                  name: "property",
                  id: "property",
                }}
              >
                <option aria-label="None" value="">
                  All
                </option>
                <option value={1}>House</option>
                <option value={0}>Flat / Apartment</option>
              </NativeSelect>
            </FormControl>
            <FormControlLabel
              control={
                <Checkbox
                  checked={STC}
                  onChange={handleOfferStatus}
                  name="checkedB"
                  color="secondary"
                />
              }
              labelPlacement="end"
              label={`Include ${filters?.sell === "Buy" ? "Sold" : "Let"}/STC`}
            />
          </div>
        </Form>
      )}
    </Formik>
  );
};

PropertySearch.propTypes = {
  withSort: PropTypes.bool,
  filters: PropTypes.object,
  length: PropTypes.number,
  handleSubmitFilters: PropTypes.func,
};

export default PropertySearch;
